import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";

import {
  ProfileType,
  applicationIsIncomplete,
  logError,
  sync as syncProfile,
  useOwnerProfile,
  useProfile,
  useTheme,
} from "@up/data";
import {
  Animation,
  Auth,
  BoundaryFallback,
  BrowserOnly,
  LoadingModal,
  Spinner,
  Toast,
  ToastTitle,
} from "@up/ui";

import { ApplicationRequired } from "./app-required";
import {
  Goals,
  HonestyPledge,
  PrivateSchoolAssistance,
  QualifiedBenefits,
  Statement,
  TypeOfSchool,
} from "./fields";
import { ScholarshipForm } from "./form";
import { LoadScholarshipData } from "./load-scholarship-data";
import { Profiles } from "./profiles";
import { ScholarshipFetchError } from "./scholarship-fetch-error";
import { ScholarshipSaveError } from "./scholarship-save-error";
import { ScholarshipThanks } from "./thanks";

import type { TScholarship } from "@up/data";
import type { ErrorInfo, ReactElement, ReactNode } from "react";

const logBoundaryError = (error: unknown, info: ErrorInfo) => {
  logError({ action: "SCHOLARSHIP_BOUNDARY_ERROR", error, context: { up_stack: info.componentStack } });
};

const FieldsDefault = () => {
  const theme = useTheme();

  return (
    <>
      <QualifiedBenefits />

      <TypeOfSchool />

      <PrivateSchoolAssistance />

      <Statement />

      <Goals />

      <HonestyPledge partnerName={theme.partnerName} />
    </>
  );
};

const Wrapper = function ({ children }: { children: ReactNode }) {
  return <div className="up-form-container pt-6">{children}</div>;
};

const Heading = function () {
  const theme = useTheme();

  return (
    <div className="mb-8">
      <h1 className={theme.heading1Class}>Scholarship Application</h1>
    </div>
  );
};

function Go({
  Fields,
  Intro,
}: {
  Fields?: ReactElement;
  Intro?: ReactElement;
}) {
  const auth = useAuthStore();
  const profile = useProfile();
  const ownerProfile = useOwnerProfile();
  const theme = useTheme();

  useEffect(() => {
    syncProfile();
  }, []);

  if (!auth.currentUser) {
    return (
      <div className="mx-auto pt-6 px-4 max-w-[600px]">
        <Heading />
        <Animation type="splash-in">
          <Auth />
        </Animation>
      </div>
    );
  }

  if (
    !profile.isLoading &&
    profile.data?.type === ProfileType.Student &&
    applicationIsIncomplete(profile.contact?.application?.status)
  ) {
    return (
      <Wrapper>
        <Heading />
        <ApplicationRequired title="You must complete your application before applying for a scholarship" />
      </Wrapper>
    );
  }
  if (
    !profile.isLoading &&
    ownerProfile &&
    profile.data?.type === ProfileType.Guardian &&
    applicationIsIncomplete(ownerProfile.application?.status)
  ) {
    return (
      <Wrapper>
        <Heading />
        <ApplicationRequired title="Your student must complete an application before applying for a scholarship" />
      </Wrapper>
    );
  }

  if (ownerProfile) {
    const SubmittingFor = () => {
      return profile?.data?.type === ProfileType.Guardian &&
        ownerProfile.profile.email !== profile.data.email ? (
        <div className="mb-4">
          <Toast variant="info">
            <ToastTitle>
              Submitting for {ownerProfile.profile.firstName}{" "}
              {ownerProfile.profile.lastName}
            </ToastTitle>
            {ownerProfile.profile.email}
          </Toast>
        </div>
      ) : null;
    };

    return (
      <Wrapper>
        <Heading />
        <LoadScholarshipData
          contactId={ownerProfile._id}
          email={ownerProfile.profile.email}
          partnerID={theme.partnerID}
        >
          {(scholarship) => {
            if (scholarship.fetchStatus === "loading") {
              return <LoadingModal />;
            }

            if (scholarship.fetchStatus === "error") {
              return <ScholarshipFetchError />;
            }

            if (scholarship?.data?.createdAt) {
              return <ScholarshipThanks scholarship={scholarship.data} />;
            }

            if (scholarship.fetchStatus === "ready") {
              return (
                <Animation type="splash-in">
                  <SubmittingFor />
                  <p className="reg-text-xl">
                    {Intro ? (
                      Intro
                    ) : (
                      <span>
                        {theme.partnerName} provides scholarships for students
                        with financial need. Please complete the application
                        below in order to be considered. You will receive
                        notification of your scholarship determination as a
                        separate email from your admissions decision, usually
                        within one week. Please note that scholarship decisions
                        are final. We strongly encourage students to consult
                        their parent/guardian or someone with greater knowledge
                        of your household financial circumstances before final
                        submission of this scholarship request form.
                      </span>
                    )}
                  </p>
                  <br />

                  <ScholarshipForm
                    defaultValues={scholarship.data as TScholarship}
                    Fields={Fields || <FieldsDefault />}
                    Bottom={() => {
                      return (
                        <>
                          <SubmittingFor />

                          <div>
                            <Toast variant="info">
                              Please be sure the information you provide is
                              complete before submitting. Scholarship
                              determinations are final.
                            </Toast>
                          </div>

                          {scholarship.saveStatus === "error" ? (
                            <div className="sticky bottom-0 z-50 py-4">
                              <ScholarshipSaveError />
                            </div>
                          ) : null}

                          <div className="flex items-center pt-4">
                            <button
                              className={theme.buttonPrimaryClass}
                              disabled={scholarship.saveStatus === "loading"}
                              type="submit"
                            >
                              Submit Scholarship Application
                            </button>

                            {scholarship.saveStatus === "loading" ? (
                              <Spinner className="ml-3" />
                            ) : null}
                          </div>
                        </>
                      );
                    }}
                    onSubmit={(newValues: Partial<TScholarship>) => {
                      const payload = {
                        ...(scholarship.data as TScholarship),
                        ...newValues,
                        partner: {
                          _id: theme.partnerID,
                          name: theme.partnerName,
                        },
                      };

                      if (profile?.data) {
                        payload.firstName = profile.data.firstName as string;
                        payload.lastName = profile.data.lastName as string;
                      }

                      scholarship.setData(payload);
                      scholarship.save(payload);
                    }}
                  />
                  <br />
                  <br />
                </Animation>
              );
            }

            return <div />;
          }}
        </LoadScholarshipData>
      </Wrapper>
    );
  } else {
    return (
      <Wrapper>
        <Heading />
        <Profiles />
      </Wrapper>
    );
  }
}

export function ScholarshipController({
  Fields,
  Intro,
}: {
  Fields?: ReactElement;
  Intro?: ReactElement;
}) {
  return (
    <BrowserOnly>
      <ErrorBoundary FallbackComponent={BoundaryFallback} onError={logBoundaryError}>
        <Go Fields={Fields} Intro={Intro} />
      </ErrorBoundary>
    </BrowserOnly>
  );
}
